// R485 client half (Dee via Rook #66417, #66468): what the new-order phone box needs to READ a
// typed number. The search itself is the server's (GET /admin/customers/search, a prefix match
// on the stored number); nothing here decides who a customer is.
//
// normalizeLebanesePhone and formatLebanesePhone are a narrow copy of master-layer's
// PhoneNormalizationService (libphonenumber-js, default country LB) number plan: the four LB type
// patterns below, read from its metadata.max.json "LB" entry. They only drive the "Reads as
// 03 123 456" line under the box: a complete Lebanese number is shown the way a shop writes it.
// If the library is bumped, re-read its LB entry against these patterns.
//
// One deliberate reading the library does not make: digits that start with 961 (no + and no 0)
// are always read as the country code, as the server's prefix planner does.

const LB_FIXED_LINE = /^(?:7(?:62|8[0-6]|9[04-9])\d{4}|(?:[14-69]\d|2(?:[14-69]\d|[78][1-9])|7[2-57]|8[02-9])\d{5})$/
const LB_MOBILE = /^(?:(?:3|81)\d|7(?:[01]\d|6[013-9]|8[7-9]|9[0-4]))\d{5}$/
const LB_PREMIUM = /^9[01]\d{6}$/
const LB_SHARED_COST = /^80\d{6}$/

function isLebaneseNational(nat: string): boolean {
  return LB_FIXED_LINE.test(nat) || LB_MOBILE.test(nat) || LB_PREMIUM.test(nat) || LB_SHARED_COST.test(nat)
}

/**
 * Any Lebanese form (03 123456, 3 123 456, 961 3 123456, +961 3-123-456, 0096170123456,
 * 70 123 456, ...) -> "+961" + national digits, or null when the input is not a complete,
 * valid Lebanese number (too short, too long, letters, another country's number).
 */
export function normalizeLebanesePhone(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null
  const compact = raw.trim().replace(/[\s\-().]/g, '')
  if (!/^\+?\d+$/.test(compact)) return null

  let nat: string
  if (compact.startsWith('+')) {
    if (!compact.startsWith('+961')) return null
    nat = compact.slice(4)
  } else if (compact.startsWith('00')) {
    if (!compact.startsWith('00961')) return null
    nat = compact.slice(5)
  } else if (compact.startsWith('961')) {
    nat = compact.slice(3)
  } else {
    nat = compact
  }
  if (nat.startsWith('0')) nat = nat.slice(1)

  return isLebaneseNational(nat) ? `+961${nat}` : null
}

/** The number as a Lebanese shop writes it: "03 123 456", "01 123 456", "70 123 456". */
export function formatLebanesePhone(normalized: string): string {
  if (!normalized.startsWith('+961')) return normalized
  const nat = normalized.slice(4)
  if (nat.length === 7) return `0${nat[0]} ${nat.slice(1, 4)} ${nat.slice(4)}`
  if (nat.length === 8) return `${nat.slice(0, 2)} ${nat.slice(2, 5)} ${nat.slice(5)}`
  return normalized
}

/**
 * How many national digits the box holds, the count the list waits on (from 2): the digits
 * with a leading 00, then a leading 961, then one trunk 0 dropped. "03" is 1, "03 4" is 2,
 * "9613" is 1. A prefix of 961 ("96") counts 2 and is sent; the server waits on it and answers
 * an empty list (Rook #66515), so the box shows nothing.
 */
export function nationalDigitCount(raw: string): number {
  let d = raw.replace(/\D/g, '')
  if (d.startsWith('00')) d = d.slice(2)
  if (d.startsWith('961')) d = d.slice(3)
  if (d.startsWith('0')) d = d.slice(1)
  return d.length
}
